import { focus, svgColor, svgUri } from '../../helpers'
import { buttonReset } from '../Button'
import { StyleFn, IconClose } from '..'

export const Chip: StyleFn = (theme) => {
  const { color, font, size } = theme
  const { focusDefault, focusWithPseudo } = focus(theme)

  const getLabelStyles = (height: any, padding: any) => ({
    paddingLeft: padding,
    paddingRight: padding,
    lineHeight: height,
  })

  const getIconStyles = (height: any, padding: any, margin?: any) => ({
    fontSize: height,
    height,
    paddingLeft: padding,
    marginRight: margin ? `-${margin}` : `calc(${padding}/-2)`,
    '& svg': {
      fontSize: height,
    },
  })

  const getRemoveBtnStyles = (height: any) => ({
    width: `calc(${height} + 2px)`,
    height: `calc(${height} + 2px)`,
    backgroundSize: height,
  })

  // FIXME use lighter/darker color tokens based on feedback color tokens ?
  // warning color is based on orange for chip, instead of yellow for all other components...
  // @see https://chat.google.com/room/AAAA7SsHhrU/UFcFlaQU_mk
  const bgColors =
    color.type === 'light'
      ? {
          info: color.palette.secondaryblue[20],
          success: color.palette.green[20],
          warning: color.palette.orange[20],
          error: color.palette.red[20],
        }
      : {
          info: color.feedback.info.default,
          success: color.palette.green[70],
          warning: color.palette.orange[70],
          error: color.palette.red[70],
        }

  return {
    root: {
      // layout
      display: 'inline-flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      justifyContent: 'center',
      verticalAlign: 'middle',
      // reset
      border: 'none',
      cursor: 'default',
      padding: 0,
      outline: 0,
      // look
      color: color.getModalValues(color.text.primary),
      backgroundColor: color.getModalValues(color.neutral.contrast.light),
      fontSize: font.size.small,
      fontWeight: font.weight.medium,
      height: size.spacing[24],
      borderRadius: size.spacing[12],
    },
    label: {
      ...getLabelStyles(size.spacing[24], size.spacing[12]),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    removeBtn: {
      extend: [buttonReset, focusWithPseudo({ radius: '50%', offset: 5 }), getRemoveBtnStyles(size.spacing[16])],
      padding: size.spacing[2],
      margin: `0 ${size.spacing[4]} 0 -${size.spacing[4]}`,
      backgroundImage: svgUri(
        svgColor(IconClose.data, color.type === 'light' ? color.palette.primaryblue['60'] : color.palette.white),
      ),
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'left 50% top 50%',
      backgroundColor: color.getModalValues(color.neutral.contrast.normal),
      borderRadius: '50%',
      '&:hover': {
        backgroundColor: color.getModalValues(color.neutral.contrast.dark),
      },
      '&:active': {
        backgroundColor: color.getModalValues(color.neutral.contrast.darker),
      },
    },
    icon: getIconStyles(size.spacing[16], size.spacing[12], size.spacing[4]),
    _clickable: {
      extend: [focusDefault],
      cursor: 'pointer',
      userSelect: 'none',
      WebkitTapHighlightColor: 'transparent',
      color: color.type === 'light' ? color.brand.normal : color.palette.white,
      '&:hover': {
        backgroundColor: color.getModalValues(color.neutral.contrast.normal),
      },
      '&:active': {
        color: color.background.interactive.primary.contrastText,
        backgroundColor: color.background.interactive.primary.hover,
      },
    },
    _info: {
      backgroundColor: bgColors.info,
    },
    _success: {
      backgroundColor: bgColors.success,
    },
    _warning: {
      backgroundColor: bgColors.warning,
    },
    _error: {
      backgroundColor: bgColors.error,
    },
    _medium: {
      fontSize: font.size.large,
      height: size.spacing[32],
      borderRadius: size.spacing[16],
      '& > $label': getLabelStyles(size.spacing[32], size.spacing[16]),
      '& > $icon': getIconStyles(size.spacing[24], size.spacing[16]),
      '& > $removeBtn': getRemoveBtnStyles(size.spacing[24]),
    },
    _small: {},
    _xsmall: {
      fontSize: font.size.xsmall,
      height: size.spacing[20],
      '& > $label': getLabelStyles(size.spacing[20], size.spacing[8]),
      '& > $icon': getIconStyles(size.spacing[12], size.spacing[8]),
      '& > $removeBtn': getRemoveBtnStyles(size.spacing[12]),
    },
  }
}
